// src/pages/settings/create-organization/orgSetup.ts
export type PlanRange = 'low' | 'high';
export type OrgPlanId = 'free' | 'base' | 'pro';
export type VideoScope = 'person' | 'team' | 'company';
export type VideoProvider = 'zoom' | 'meet';
/** monthly = billed every month. semester / annual = 6 / 12 months paid up front at a lower per-seat price (see orderQuote.ts). */
export type BillingCycle = 'monthly' | 'semester' | 'annual';

// [PRICING] Each extension has an on/off flag plus a chosen quantity. Billing uses the QUANTITY, never the structure count.
// phone -> voice -> voicePro are nested: voice and voicePro need phone on, and each qty is capped by phoneQty.
// video* describes Zoom: included (we sell it) or own (they bring it); scope = per person / team / company; videoQty = how many.
export interface OrgAddons {
  phone: boolean;
  phoneQty: number;
  voice: boolean;
  voiceQty: number;
  waSeller: boolean;
  waSellerQty: number;
  waTeam: boolean;
  waTeamQty: number;
  waCompany: boolean;
  waCompanyQty: number;
  voicePro: boolean;
  voiceProQty: number;
  /** Set after the first visit, so a chosen quantity of 1 is kept. */
  qtyChosen?: boolean;
  videoIncluded: boolean;
  videoProvider: VideoProvider;
  videoScope: VideoScope;
  videoQty: number;
  videoOwn: boolean;
  videoOwnProvider: VideoProvider;
}

// [HANDOFF] Everything chosen after the structure questions. `range`, `created`, `createdName`, `billingName`
// and `billingEmail` belong to the older logged-in flow; the public flow mainly uses planId, cycle and addons.
export interface OrgSetup {
  range: PlanRange;
  planId: OrgPlanId;
  addons: OrgAddons;
  billingName: string;
  billingEmail: string;
  created: boolean;
  createdName: string;
  /** The billing choice. Read it through billingCycle() in orderQuote.ts, which also understands old saves that only have `yearly`. */
  cycle?: BillingCycle;
  /** [HANDOFF] Legacy flag, kept equal to `cycle === 'annual'` so older saved setups still load. New code reads `cycle`. */
  yearly?: boolean;
}

// [HANDOFF] localStorage key for the whole plan + extensions choice (shape = OrgSetup above). Survives refresh, not logout.
const KEY = 'ventaje_org_setup_v1';

// [PRICING] Starting state: every paid extension is off - the phone line, voice, the WhatsApp API and Zoom (QA-F06, Q-4 (a): a first
// visit adds nothing to the price). Personal WhatsApp is always included and free, so it has no switch. The page tells people they
// can add more later. A saved setup keeps whatever was saved (loadOrgSetup merges it over these defaults).
// Quantities start at 1 and are reset to the real counts (advisors/teams/companies) on the first visit to the extensions page.
export function defaultAddons(): OrgAddons {
  return {
    phone: false,
    phoneQty: 1,
    voice: false,
    voiceQty: 1,
    waSeller: false,
    waSellerQty: 1,
    waTeam: false,
    waTeamQty: 1,
    waCompany: false,
    waCompanyQty: 1,
    voicePro: false,
    voiceProQty: 1,
    videoIncluded: false,
    videoProvider: 'zoom',
    videoScope: 'company',
    videoQty: 1,
    videoOwn: false,
    videoOwnProvider: 'zoom',
  };
}

// [HANDOFF] Reads the saved setup and fills any missing field with the defaults below (planId defaults to 'base').
export function loadOrgSetup(): OrgSetup {
  const base: OrgSetup = {
    range: 'low',
    planId: 'base',
    addons: defaultAddons(),
    billingName: '',
    billingEmail: '',
    created: false,
    createdName: '',
  };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<OrgSetup>;
    return {
      ...base,
      ...parsed,
      addons: { ...base.addons, ...(parsed.addons ?? {}) },
    };
  } catch {
    return base;
  }
}

export function saveOrgSetup(next: OrgSetup): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

// [HANDOFF] Forgets the saved plan + extensions choice. The return page calls it once the application reads paid or active
//   (the structure draft has its own clear); KEY stays private to this file.
export function clearOrgSetup(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
